import { useState, useEffect } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { birdsApi, Bird } from '../../api/birds';
import {
  BrandModal,
  BrandModalBody,
  BrandModalHeader,
} from '../ui/BrandModal';

interface BirdSelectorProps {
  onBirdSelect: (birdId: string) => void;
  selectedBirdId: string | null;
  onClear: () => void;
}

export default function BirdSelector({ onBirdSelect, selectedBirdId, onClear }: BirdSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [birds, setBirds] = useState<Bird[]>([]);
  const [filteredBirds, setFilteredBirds] = useState<Bird[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedBird, setSelectedBird] = useState<Bird | null>(null);

  useEffect(() => {
    loadBirds();
  }, []);

  useEffect(() => {
    if (selectedBirdId && birds.length > 0) {
      const bird = birds.find(b => b.id === selectedBirdId);
      setSelectedBird(bird || null);
    }
  }, [selectedBirdId, birds]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredBirds(birds.slice(0, 50)); // Show first 50 birds when no search
    } else {
      const filtered = birds.filter(bird =>
        bird.bandId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (bird.species && bird.species.commonName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (bird.cageNumber && bird.cageNumber.toLowerCase().includes(searchTerm.toLowerCase()))
      ).slice(0, 20); // Limit to 20 results
      setFilteredBirds(filtered);
    }
  }, [searchTerm, birds]);

  const loadBirds = async () => {
    try {
      setLoading(true);
      const response = await birdsApi.getBirds();
      setBirds(response || []);
    } catch (error) {
      console.error('Failed to load birds:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBirdSelect = (bird: Bird) => {
    setSelectedBird(bird);
    onBirdSelect(bird.id);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = () => {
    setSelectedBird(null);
    onClear();
    setSearchTerm('');
  };

  const formatBirdAge = (hatchingDate?: Date) => {
    if (!hatchingDate) return 'Unknown age';
    
    const today = new Date();
    const hatchDate = new Date(hatchingDate);
    const diffTime = Math.abs(today.getTime() - hatchDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) return `${diffDays} days old`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months old`;
    return `${Math.floor(diffDays / 365)} years old`;
  };

  const getGenderColor = (gender: string) => {
    switch (gender.toUpperCase()) {
      case 'MALE': return 'text-cyan-600 bg-blue-100';
      case 'FEMALE': return 'text-pink-600 bg-pink-100';
      default: return 'text-slate-600 bg-gray-100';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'BREEDING': return 'text-green-600 bg-green-100';
      case 'AVAILABLE': return 'text-cyan-600 bg-blue-100';
      case 'SOLD': return 'text-slate-600 bg-gray-100';
      case 'DECEASED': return 'text-red-600 bg-red-100';
      default: return 'text-slate-600 bg-gray-100';
    }
  };

  return (
    <div className="relative">
      {/* Selected Bird Display or Search Button */}
      {selectedBird ? (
        <div className="flex items-center justify-between p-4 bg-white border border-gray-300 rounded-lg shadow-sm">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                {selectedBird.species?.commonName?.charAt(0).toUpperCase() || 'B'}
              </div>
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-semibold text-slate-900">{selectedBird.bandId}</h3>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getGenderColor(selectedBird.gender)}`}>
                  {selectedBird.gender}
                </span>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedBird.status)}`}>
                  {selectedBird.status}
                </span>
              </div>
              <p className="text-sm text-slate-600">{selectedBird.species?.commonName || 'Unknown Species'}</p>
              <p className="text-xs text-slate-500">
                {selectedBird.cageNumber && `Cage: ${selectedBird.cageNumber} • `}
                {formatBirdAge(selectedBird.hatchingDate)}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsOpen(true)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
            >
              Change
            </button>
            <button
              onClick={handleClear}
              className="inline-flex items-center p-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setIsOpen(true)}
          className="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
        >
          <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
          Select a bird to generate certificate
        </button>
      )}

      {/* Bird Selection Modal */}
      <BrandModal open={isOpen} onClose={() => setIsOpen(false)} maxWidthClass="max-w-2xl">
        <BrandModalHeader title="Select a Bird" onClose={() => setIsOpen(false)} />
        <BrandModalBody>
                {/* Search Input */}
                <div className="flex items-center w-full mb-4 rounded-md border border-gray-300 bg-white focus-within:ring-1 focus-within:ring-cyan-500 focus-within:border-primary-500">
                  <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
                    <MagnifyingGlassIcon className="h-5 w-5" />
                  </span>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1 min-w-0 pr-3 py-2 border-0 bg-transparent leading-5 placeholder-gray-500 focus:outline-none focus:ring-0 focus:placeholder-gray-400"
                    placeholder="Search by band ID, species, or cage number..."
                  />
                </div>

                {/* Birds List */}
                <div className="max-h-96 overflow-y-auto">
                  {loading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                      <p className="mt-2 text-sm text-slate-600">Loading birds...</p>
                    </div>
                  ) : filteredBirds.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-sm text-slate-600">
                        {searchTerm ? 'No birds found matching your search.' : 'No birds available.'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredBirds.map((bird) => (
                        <button
                          key={bird.id}
                          onClick={() => handleBirdSelect(bird)}
                          className="w-full flex items-center p-3 text-left hover:bg-slate-50/80 rounded-lg border border-transparent hover:border-gray-200 transition-colors"
                        >
                          <div className="flex-shrink-0 mr-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                              {bird.species?.commonName?.charAt(0).toUpperCase() || 'B'}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <p className="text-sm font-semibold text-slate-900 truncate">{bird.bandId}</p>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getGenderColor(bird.gender)}`}>
                                {bird.gender}
                              </span>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(bird.status)}`}>
                                {bird.status}
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 truncate">{bird.species?.commonName || 'Unknown Species'}</p>
                            <p className="text-xs text-slate-500">
                              {bird.cageNumber && `Cage: ${bird.cageNumber} • `}
                              {formatBirdAge(bird.hatchingDate)}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
        </BrandModalBody>
      </BrandModal>
    </div>
  );
} 